import { Dropdown, Icon, IconButton, PrimaryButton, Stack, TextField, TooltipHost } from '@fluentui/react';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { createNewGuid } from '../components/Utils';
import { ApiCalls } from '../api/api';
import { useSuspenseQuery } from '@tanstack/react-query';
import { APPROVER_CUSTOM_FILTER } from '../constants/queryKeys';
import { ApproverCustomFiltersRequest } from '../api/generated/data-contracts';
import { useSetQueryData } from '../hooks/useSetQueryData';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { useTranslation } from 'react-i18next';

type ApproverCustomFiltersContainerProps = {
    currentURPStateFilterArr: string[];
    currentUserFilterArr: string[];
    customFilterChanged: boolean;
    applyFilter: (appliedFilter: ApproverCustomFiltersRequest) => void;
    filterSaved: () => void;
};

const approverQueryKey = [APPROVER_CUSTOM_FILTER];

const itemStyles: React.CSSProperties = {
    alignItems: 'left',
    display: 'flex',
    height: 40,
    width: 1000,
    justifyContent: 'left',
};

function ApproverCustomFiltersContainer({
    applyFilter,
    currentURPStateFilterArr: currentURPStateFilterArrProps,
    currentUserFilterArr: currentUserFilterArrProps,
    customFilterChanged,
    filterSaved,
}: ApproverCustomFiltersContainerProps) {
    const { t } = useTranslation('approval');
    const [saveAsInitiated, setSaveAsInitiated] = useState<boolean>(false);
    const [renameInitiated, setRenameInitiated] = useState<boolean>(false);
    const [nameInputValue, setNameInputValue] = useState<string>('');
    const defaultCustomFilters = useRef([] as ApproverCustomFiltersRequest[]).current;
    const { data: customFilters = defaultCustomFilters } = useSuspenseQuery({
        queryKey: approverQueryKey,
        queryFn: () => ApiCalls.getByUser().then(res => res.data),
    });
    const setCustomFilters = useSetQueryData<typeof customFilters>(approverQueryKey);
    const [selectedCustomFilter, setSelectedCustomFilter] = useState<string | null>(() => customFilters.find(filter => filter.default)?.id || null);

    const [currentURPStateFilterArr, setCurrentURPStateFilterArr] = useState<string[]>(currentURPStateFilterArrProps);
    const [currentUserFilterArr, setCurrentUserFilterArr] = useState<string[]>(currentUserFilterArrProps);

    const [filterChanged, setFilterChanged] = useState<boolean>(customFilterChanged);

    const noCustomFilterOption = useMemo(() => ({ key: 'nocustomfilter', text: t('filter.custom.input.placeholder') }), [t]);

    useEffect(() => {
        setCurrentURPStateFilterArr(currentURPStateFilterArrProps);
        setCurrentUserFilterArr(currentUserFilterArrProps);
    }, [currentURPStateFilterArrProps, currentUserFilterArrProps]);

    useEffect(() => {
        if (customFilterChanged === null || customFilterChanged === undefined) return;
        setFilterChanged(customFilterChanged);
    }, [customFilterChanged]);

    useEffect(() => {
        if (customFilters.length < 1 || selectedCustomFilter === null) {
            applyFilter(null);
            return;
        }
        const filterToApply = customFilters.filter(_ => _.id === selectedCustomFilter)[0];
        if (!filterToApply) {
            applyFilter(null);
            return;
        }
        applyFilter(filterToApply);
    }, [selectedCustomFilter, customFilters, applyFilter]);

    const {
        initialData: {
            aggregatedUser: { user },
        },
    } = useConfigValuesState();

    const customFilterSaveAs = async () => {
        const filterName = nameInputValue;
        const cURPSF = currentURPStateFilterArr.filter(_ => _ != 'selectalloption');
        const cUsersF = currentUserFilterArr.filter(_ => _ != 'selectalloption');

        let newUid = null;
        try {
            newUid = window.crypto.randomUUID();
        } catch (err) {
            newUid = createNewGuid();
        }

        const newACF: ApproverCustomFiltersRequest = {
            id: newUid,
            name: filterName,
            default: false,
            userId: user.id,
            urpStateFilter: cURPSF,
            approverUsersFilter: cUsersF,
        };

        try {
            await ApiCalls.saveApprover(newACF);

            setCustomFilters(prev => [...prev, newACF]);
            setSelectedCustomFilter(newUid);
        } catch (error) {
            console.error(error);
        }

        setNameInputValue('');
        setSaveAsInitiated(false);
    };

    const customFilterSave = async () => {
        const filterToSaveId = selectedCustomFilter;
        const cURPSF = currentURPStateFilterArr.filter(_ => _ != 'selectalloption');
        const cUsersF = currentUserFilterArr.filter(_ => _ != 'selectalloption');

        // const changedACF = {
        //     id: filterToSaveId,
        //     urpStateFilter: cURPSF,
        //     approverUsersFilter: cUsersF,
        // } as ApproverCustomFilter;
        const changedACF: ApproverCustomFiltersRequest = {
            id: filterToSaveId,
            urpStateFilter: cURPSF,
            approverUsersFilter: cUsersF,
            default: false,
            name: nameInputValue,
            userId: user.id,
        };

        await ApiCalls.save(changedACF);

        setCustomFilters(prev =>
            prev.map(filter => {
                if (filter.id === filterToSaveId) {
                    return {
                        ...filter,
                        urpStateFilter: changedACF.urpStateFilter,
                        approverUsersFilter: changedACF.approverUsersFilter,
                    };
                }
                return filter;
            }),
        );
        filterSaved();
    };

    const customFilterRename = async () => {
        const filterName = nameInputValue;
        const filterToRenameId = selectedCustomFilter;

        try {
            await ApiCalls.renameApproverCustomFilter({ id: filterToRenameId, name: filterName });

            setCustomFilters(prev =>
                prev.map(filter => {
                    if (filter.id === filterToRenameId) {
                        return {
                            ...filter,
                            name: filterName,
                        };
                    }
                    return filter;
                }),
            );
        } catch (error) {
            console.error(error);
        }

        setNameInputValue('');
        setRenameInitiated(false);
    };

    const customFilterSetDefault = async (filterId: string) => {
        if (filterId === noCustomFilterOption.key) filterId = null;
        await ApiCalls.makeDefault(filterId);
        setCustomFilters(filters =>
            filters.map(filter => {
                return {
                    ...filter,
                    default: filter.id === filterId,
                };
            }),
        );
    };

    const customFilterDelete = async (id: string) => {
        await ApiCalls.delete({ id });
        setCustomFilters(prev => prev.filter(_ => _.id !== id));
    };

    return (
        <Stack horizontal horizontalAlign="space-between" styles={{ root: { width: '100%' } }}>
            <span style={itemStyles}>
                <Dropdown
                    placeholder={t('filter.custom.input.placeholder')}
                    options={[
                        noCustomFilterOption,
                        ...customFilters.map(cf => {
                            return { key: cf.id, text: cf.name };
                        }),
                    ]}
                    selectedKey={selectedCustomFilter}
                    styles={{ dropdown: { width: 354, margin: '0px 15px 0px 0px' } }}
                    onChange={(e, o, i) => {
                        setSelectedCustomFilter(o.key as string);
                    }}
                    onRenderOption={(props, defaultRender) => {
                        let isDefault = (customFilters.filter(_ => _.default)[0]?.id ?? null) === props.key;
                        const isSelected = selectedCustomFilter === props.key;
                        const isNoCustomFilter = props.key === 'nocustomfilter';
                        if (isNoCustomFilter && customFilters.filter(_ => _.default).length < 1) isDefault = true;
                        const favoriteIconFriendlyName = isDefault ? 'FavoriteStarFill' : 'FavoriteStar';
                        return (
                            <>
                                {defaultRender(props)}
                                <div style={{ position: 'absolute', right: '0px' }}>
                                    <IconButton
                                        iconProps={{ iconName: favoriteIconFriendlyName }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (!isDefault) {
                                                customFilterSetDefault(props.key as string);
                                            } else {
                                                customFilterSetDefault(null);
                                            }
                                        }}
                                    />
                                    {isNoCustomFilter ? (
                                        <></>
                                    ) : isSelected ? (
                                        <TooltipHost
                                            content={
                                                <div>
                                                    {t('filter.custom.tooltip.warning')}
                                                    <br />
                                                    {t('filter.custom.tooltip.cta')}
                                                </div>
                                            }
                                            closeDelay={250}
                                        >
                                            <IconButton
                                                iconProps={{ iconName: 'Delete' }}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                }}
                                                styles={{
                                                    icon: { color: 'rgb(200, 200, 200) !important' },
                                                    iconHovered: { color: 'rgb(200, 200, 200) !important' },
                                                    iconDisabled: { color: 'rgb(200, 200, 200) !important' },
                                                }}
                                            />
                                        </TooltipHost>
                                    ) : (
                                        <IconButton
                                            iconProps={{ iconName: 'Delete' }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                if (!isSelected) customFilterDelete(props.key as string);
                                            }}
                                        />
                                    )}
                                </div>
                            </>
                        );
                    }}
                    onRenderTitle={(props, defaultRender) => {
                        let isDefault = (customFilters.filter(_ => _.default)[0]?.id ?? null) === props[0].key;
                        const isNoCustomFilter = props[0].key === 'nocustomfilter';
                        if (isNoCustomFilter && customFilters.filter(_ => _.default).length < 1) isDefault = true;
                        const favoriteIconFriendlyName = isDefault ? 'FavoriteStarFill' : 'FavoriteStar';
                        return (
                            <div style={{ display: 'flex', position: 'relative' }}>
                                <div>
                                    {defaultRender(props)}
                                    {filterChanged && ' * (Modified)'}
                                </div>
                                <div style={{ position: 'absolute', right: '0px' }}>
                                    <Icon
                                        iconName={favoriteIconFriendlyName}
                                        styles={{ root: { color: 'rgb(96, 97, 170)' } }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (!isDefault) {
                                                customFilterSetDefault(props[0].key as string);
                                            } else {
                                                customFilterSetDefault(null);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    }}
                />
                {!saveAsInitiated && !renameInitiated && (
                    <PrimaryButton
                        text={t('filter.custom.button.save')}
                        width={100}
                        styles={{ root: { margin: '0px 15px 0px 0px' } }}
                        disabled={selectedCustomFilter === null || !filterChanged || selectedCustomFilter === noCustomFilterOption.key}
                        onClick={customFilterSave}
                    />
                )}
                {(saveAsInitiated || renameInitiated) && (
                    <TextField
                        placeholder={t('filter.custom.newName')}
                        width={300}
                        styles={{ root: { margin: '0px 15px 0px 0px' } }}
                        value={nameInputValue}
                        onChange={(e, n) => setNameInputValue(n)}
                    />
                )}
                {saveAsInitiated ? (
                    <></>
                ) : renameInitiated ? (
                    <PrimaryButton
                        text={t('filter.custom.button.rename')}
                        width={100}
                        styles={{ root: { margin: '0px 15px 0px 0px' } }}
                        disabled={selectedCustomFilter === null || selectedCustomFilter === noCustomFilterOption.key}
                        onClick={customFilterRename}
                    />
                ) : (
                    <PrimaryButton
                        text={t('filter.custom.button.rename')}
                        width={100}
                        styles={{ root: { margin: '0px 15px 0px 0px' } }}
                        disabled={selectedCustomFilter === null || selectedCustomFilter === noCustomFilterOption.key}
                        onClick={() => setRenameInitiated(true)}
                    />
                )}
                {renameInitiated ? (
                    <></>
                ) : saveAsInitiated ? (
                    <PrimaryButton
                        text={t('filter.custom.button.saveas')}
                        width={100}
                        styles={{ root: { margin: '0px 15px 0px 0px' } }}
                        onClick={customFilterSaveAs}
                    />
                ) : (
                    <PrimaryButton
                        text={t('filter.custom.button.saveas')}
                        width={100}
                        styles={{ root: { margin: '0px 15px 0px 0px' } }}
                        onClick={() => setSaveAsInitiated(true)}
                    />
                )}
                {(saveAsInitiated || renameInitiated) && (
                    <PrimaryButton
                        text="Cancel"
                        width={300}
                        styles={{ root: { margin: '0px 15px 0px 0px' } }}
                        onClick={() => {
                            setSaveAsInitiated(false);
                            setRenameInitiated(false);
                        }}
                    />
                )}
            </span>
        </Stack>
    );
}

export default ApproverCustomFiltersContainer;
